import { css } from "@emotion/core"
import { graphql } from "gatsby"
import { RichText } from "prismic-reactjs"
import React from "react"
import { ReactComponent as CloudBackgroundSvg } from "../assets/images/cloud-background.svg"
import { ReactComponent as CompanyBackgroundOneSvg } from "../assets/images/company-background1.svg"
import { ReactComponent as CompanyBackgroundTwoSvg } from "../assets/images/company-background2.svg"
import {
  Animate,
  Container,
  Faqs,
  Heading,
  Layout,
  PageSection,
  Section,
  Seo,
  Text
} from "../components"
import { BACKGROUND_OFFTEAL } from "../components/theme"

export const query = graphql`
  query($uid: String!) {
    prismic {
      about(uid: $uid, lang: "en-us") {
        seo_title
        seo_description
        seo_image
        header_title
        header_body
        header_image
        team_title
        team_body
        team_image
        who_title
        who_body
        who_image
        products_title
        products {
          description
          image
          subtitle
          title
        }
      }
      nav(lang: "en-us", uid: "nav") {
        logo
        links {
          link {
            ...PrismicLinkAbout
          }
          text
        }
        cta_text
        cta_link {
          ...PrismicLinkAbout
        }
      }
      faq(lang: "en-us", uid: "faqs") {
        heading
        items {
          answer
          question
        }
        faq_expand_prompt
        chat_support_prompt
        chat_support_image
        chat_support_heading
      }
      footer(lang: "en-us", uid: "footer") {
        logo
        nav_links {
          link {
            ...PrismicLinkAbout
          }
          text
        }
        subscribe_form_heading
        subscribe_form_input
        subscribe_form_prompt
        info_links {
          link {
            ...PrismicLinkAbout
          }
          text
        }
      }
    }
  }
  fragment PrismicLinkAbout on PRISMIC__Linkable {
    _linkType
    ... on PRISMIC_Demo {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Faq {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Features {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Testimonials {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Contact {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Home {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Privacy {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Signup {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Terms {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_About {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Footer {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC_Pricing {
      _meta {
        uid
        type
      }
    }
    ... on PRISMIC__ExternalLink {
      url
    }
    ... on PRISMIC__ImageLink {
      url
    }
    ... on PRISMIC__FileLink {
      url
    }
  }
`

const AboutPage = props => {
  const { about } = props.data.prismic

  return (
    <Layout {...props}>
      <Seo {...about} />
      <PageSection
        article={
          <div>
            <Animate x={10} delay={0}>
              <Heading.H1>{RichText.asText(about.header_title)}</Heading.H1>
            </Animate>
            <Animate x={20} delay={10}>
              <Text.Large>{RichText.asText(about.header_body)}</Text.Large>
            </Animate>
          </div>
        }
        background={
          <CompanyBackgroundOneSvg
            css={css`
              width: 100vw !important;
              height: 20vw !important;
              margin-top: 5px;
            `}
          />
        }
        illustration={
          <Animate x={20}>
            <img src={about.header_image.url} />
          </Animate>
        }
      />

      <PageSection
        reverseColumns
        articleWidth="50%"
        article={
          <div>
            <Animate x={20} delay={0} offset={-100}>
              <Heading.H1>{RichText.asText(about.team_title)}</Heading.H1>
            </Animate>
            <Animate x={20} delay={10} offset={-100}>
              <Text.Large>{RichText.asText(about.team_body)}</Text.Large>
            </Animate>
          </div>
        }
        illustration={
          <Animate x={-20} offset={-100}>
            <img
              css={css`
                left: 0 !important;
                right: 0 !important;
                margin: 0 auto;
                max-width: 100%;
                max-height: 100%;
                height: auto !important;
              `}
              src={about.team_image.url}
              alt="Company Picture"
            />
          </Animate>
        }
        alignIllustrationTop
        background={
          <CompanyBackgroundTwoSvg
            css={css`
              width: 100vw !important;
              height: 7.3vw !important;
              z-index: 10;
            `}
          />
        }
        backgroundColor={BACKGROUND_OFFTEAL}
      />

      <PageSection
        article={
          <div>
            <Animate x={10} delay={0} offset={-100}>
              <Heading.H1>{RichText.asText(about.who_title)}</Heading.H1>
            </Animate>
            <Animate x={20} delay={20} offset={-100}>
              <Text.Large>{RichText.asText(about.who_body)}</Text.Large>
            </Animate>
          </div>
        }
        illustration={
          <Animate x={20} offset={-100}>
            <img src={about.who_image.url} style={{ maxHeight: 350 }} />
          </Animate>
        }
        alignIllustrationTop
        background={
          <CloudBackgroundSvg
            css={css`
              width: 100vw !important;
              height: 10vw !important;
              z-index: 10;
            `}
          />
        }
      />

      <Section css={styles.sectionApps}>
        <Container style={{ maxWidth: 900 }}>
          <Heading.H1>{RichText.asText(about.products_title)}</Heading.H1>
          <ul>
            {about.products.map((x, i) => (
              <Animate key={i} x={50} delay={50} offset={-100}>
                <li>
                  <figure>
                    <img src={x.image.url} alt="app icon" />
                  </figure>
                  <div>
                    <Heading.H5>{RichText.asText(x.title)}</Heading.H5>
                    <Heading.H6>{RichText.asText(x.subtitle)}</Heading.H6>
                    <Text.Small>{RichText.asText(x.description)}</Text.Small>
                  </div>
                </li>
              </Animate>
            ))}
          </ul>
        </Container>
      </Section>

      <Faqs {...props} adjacentToFooter />
    </Layout>
  )
}

const styles = {
  sectionApps: css`
    background-color: white;
    h1 {
      margin-top: 40px;
      text-align: center;
    }
    ul {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    li {
      margin: 50px 0;
      display: flex;
      align-items: center;
      figure {
        width: 200px;
        height: 150px;
        text-align: center;
        margin-right: 40px;
        flex-shrink: 0;
        img {
          height: 100%;
          width: auto;
        }
      }
      h6 {
        margin-top: 5px;
        text-transform: none;
      }
      p {
        margin-top: 20px;
      }
      @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
        figure {
          height: 100px;
          width: 100%;
          text-align: center;
          margin-bottom: 20px;
          svg {
            margin-left: 10px;
          }
        }
      }
    }
  `
}

export default AboutPage
